
      import { UserManager } from "@albelli/oidc";
      import Cookies from "js-cookie";

      const ANONYMOUS_COOKIE = "anonymousSessionId";
      const ANONYMOUS_PENDING_KEY = "anonymous_pending_url";
      const AUTH_METHOD_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/authenticationmethod";
      const OIDC_STATE_PARAM_REGEX = /\bstate=/;

      let anonymousSessionId = Cookies.get(ANONYMOUS_COOKIE);

      // Helper to detect if URL contains OIDC state parameter (logged in or guest flow)
      const isOidcRedirect = () => OIDC_STATE_PARAM_REGEX.test(window.location.href);

      // Store the original URL before redirecting for anonymous login
      function storeOriginalURLForAnonymousIfNeeded() {
        try {
          if (!sessionStorage.getItem(ANONYMOUS_PENDING_KEY) && !isOidcRedirect()) {
            sessionStorage.setItem(ANONYMOUS_PENDING_KEY, window.location.href);
          }
        } catch {
          /* Ignore sessionStorage errors */
        }
      }

      // Restore the original URL (with query params) after returning from anonymous login
      function maybeRestoreAnonymousUrl() {
        try {
          const pending = sessionStorage.getItem(ANONYMOUS_PENDING_KEY);
          if (!pending) return;

          // Localhost workaround: login page can't set cookies for localhost, so we generate one here
          if (IS_LOCAL && !isOidcRedirect() && !anonymousSessionId) {
            const mockSessionId = crypto.randomUUID();
            Cookies.set(ANONYMOUS_COOKIE, mockSessionId);
            anonymousSessionId = mockSessionId;
          }

          if (anonymousSessionId && pending !== window.location.href) {
            history.replaceState(history.state, "", pending);
          }
          sessionStorage.removeItem(ANONYMOUS_PENDING_KEY);
        } catch {
          /* ignore storage issues */
        }
      }

      const CHANNELS = {
        nl: "albelli.nl",
        be: "albelli.be",
        de: "albelli.de",
        pxxl_de: "posterxxl.de",
        pxxl_at: "posterxxl.at",
        b2b_pxxl_de: "b2b.posterxxl.de",
        se: "onskefoto.se",
        no: "fotoknudsen.no",
        photobox_uk: "photobox.co.uk",
        photobox_fr: "photobox.fr",
        photobox_ie: "photobox.ie",
        photobox_it: "photobox.it",
        photobox_dk: "photobox.dk",
        hofmann_es: "hofmann.es",
        hofmann_pt: "hofmann.pt",
      };

      const CHANNELS_WITH_ANONYMOUS = [
        CHANNELS.be,
        CHANNELS.nl,
        CHANNELS.se,
        CHANNELS.no,
        CHANNELS.hofmann_es,
        CHANNELS.hofmann_pt,
        CHANNELS.photobox_uk,
        CHANNELS.photobox_it,
        CHANNELS.photobox_fr,
        CHANNELS.photobox_ie,
        CHANNELS.photobox_dk,
      ];

      const GUEST_FLOW_CHANNELS = [CHANNELS.pxxl_de, CHANNELS.pxxl_at];

      const getSafeChannelName = (channel) => {
        return Object.values(CHANNELS).includes(channel?.toLowerCase()) ? channel : null;
      };

      let params = new URLSearchParams(window.location.search);

      const IS_LOCAL = location.host.includes("localhost");
      const IS_ACCEPTANCE = location.host.includes("a-dtap");
      const IS_TEST = location.host.includes("t-dtap");

      const CHANNEL_NAME = (
        location.host.split("editor.")[1] ||
        getSafeChannelName(params.get("channel")) ||
        CHANNELS.photobox_uk
      ).toLowerCase();

      const isPhotobook = params.get("articleType") ? /photobook/i.test(params.get("articleType")) : true;

      const getOctopusVar = (name, fallback = "") => (!name.includes("#{") ? name : fallback);

      // Anonymous flow enabled only for specific channels (We'll start with BE only)
      const anonymousFlowEnabled = CHANNELS_WITH_ANONYMOUS.includes(CHANNEL_NAME);

      let authenticationEnabled = getOctopusVar("#{Authentication.Enabled}", "true") === "true";

      if (params.get("da") === "true") {
        authenticationEnabled = false;
      }

      // Guest-flow channels (posterxxl.de, posterxxl.at) don't require login for non-photobook products
      // All other channels use either anonymous or regular authentication
      if (GUEST_FLOW_CHANNELS.includes(CHANNEL_NAME) && !isPhotobook) {
        authenticationEnabled = false;
      }

      window.userManager = {
        accountId: "",
        isGuest: true,
        client: null,
      };

      async function bootstrap() {
        const inAppExperience = params.get("origin") === "inapp";
        maybeRestoreAnonymousUrl();

        // Disable authentication if anonymous session exists (from cookie or localhost workaround)
        if (anonymousSessionId) {
          authenticationEnabled = false;
        }

        if (authenticationEnabled) {
          const extra = {
            theme: CHANNEL_NAME,
          };

          // Disabling guest flow for in-app experience
          if (GUEST_FLOW_CHANNELS.includes(CHANNEL_NAME) && !inAppExperience) {
            extra["login_hint"] = "guest_editor";
          }

          // Disabling anonymous flow for in-app experience
          if (anonymousFlowEnabled && !inAppExperience) {
            extra["login_hint"] = "anonymous";
            storeOriginalURLForAnonymousIfNeeded();
          }

          window.userManager.client = new UserManager({
            authority: getOctopusVar("#{Authentication.Authority}", "https://t-dtap.login.{channel}/v2").replace(
              "{channel}",
              CHANNEL_NAME,
            ),
            client_type: "AuthorizationCodeWithProofKey",
            client_id: getOctopusVar("#{Authentication.ClientId}", "adf801f9c1fa41e2a8a7a92cf0bbf618"),
            client_secret: getOctopusVar(
              "#{Authentication.ClientSecret}",
              "MzJiNDRhM2E4MzBlNDA3MDg4ZDdhZWE0YTUxOGRmNzM",
            ),
            scope:
              "openid offline_access http://apis.albumprinter.com/editor/project/v1/project.read http://apis.albumprinter.com/editor/project/v1/project.write storage.upload.write storage.upload.read storage.images.read storage.images.write",
            extra,
            claims: [AUTH_METHOD_CLAIM],
          });

          const userData = await window.userManager.client.signin();
          // If we get here, it's a normal authenticated (non-anonymous) flow with tokens
          window.userManager.isGuest = userData?.[AUTH_METHOD_CLAIM] === "guest";
          window.userManager.accountId = userData?.sub || "";
          window.userManager.email = userData?.email || "";
        }

        // Load the ARC3 library css before the main css and lazy loaded custom css files
        document.head.appendChild(
          Object.assign(document.createElement("link"), {
            href: "https://arc3.sf.albelli.com/dist/cdn/arc.css",
            rel: "stylesheet",
          }),
        );
        // Load the app — Vite resolves this to the hashed output at build time
        await import("./index.tsx");
      }

      bootstrap();
    